import React, { useEffect } from 'react';
import { graphql, HeadProps, PageProps } from 'gatsby';
import { SEO } from '../../components/Seo';
import { Kfh_ContentPageUmbracoType, Kfh_ContentResourceArticleType } from '../../types/graphqlTypes';
import { ComponentRenderer } from '../../components/componentRenderer/ComponentRenderer';
import { Typography } from '../../components/Typography';
import { PageBackButton } from '../../components/PageBackButton';
import { Section } from '../../components/Section';
import { Spacer } from '../../components/Spacer';
import { SideAndMain } from '../../components/layout/SideAndMain';
import { HubNavigation } from '../../components/HubNavigation';
import { useExperiments } from '../../context/ExperimentsContext';

interface ResourceArticlePageProps {
  page: Kfh_ContentPageUmbracoType;
  stampDutyTestPage: Kfh_ContentPageUmbracoType;
}

const ResourceArticlePage = ({ data }: PageProps<ResourceArticlePageProps>) => {
  const { page } = data;
  const { layout, pageName, parentPages, id } = page;
  const { title, leadText, regions, resourceHub } = layout as unknown as Kfh_ContentResourceArticleType;

  // Get last item in parent array
  const parentPage = parentPages && parentPages.length > 0 ? parentPages[parentPages.length - 1] : null;

  // Regions
  const narrowRegion = regions.filter((region) => region.regionType === 'Narrow')[0];
  const wideRegion = regions.filter((region) => region.regionType === 'Wide')[0];

  // Split leadText into paragraphs if it exists
  const renderLeadText = () => {
    if (!leadText) return null;

    // Split the text by newlines
    const paragraphs = leadText.split(/\n+/);

    return (
      <>
        {/* First paragraph as lead */}
        {paragraphs.length > 0 && (
          <Typography component="p" variant="lead">
            {paragraphs[0]}
          </Typography>
        )}

        {/* Subsequent paragraphs as bodyLarge */}
        {paragraphs.slice(1).map((paragraph, index) => (
          <Typography key={index} component="p" variant="bodyLarge">
            {paragraph}
          </Typography>
        ))}
      </>
    );
  };

  // Side navigation

  return (
    <>
      {/* Back button */}
      {parentPage && <PageBackButton url={parentPage?.relativeUrl} label={parentPage?.pageName} />}

      {/* Main and side section */}
      <Section contained>
        <SideAndMain>
          <div>
            {/* Page Header */}
            <Spacer spacing="half">
              <Typography component="h1" variant="displaySmall">
                {title || pageName}
              </Typography>
              {leadText && renderLeadText()}
            </Spacer>

            <ComponentRenderer components={narrowRegion.components} width="narrow" />
          </div>

          {/* Side navigation */}
          {resourceHub ? (
            <HubNavigation currentPageId={id} resourceHub={resourceHub} templateName="ContentResourceArticleType" />
          ) : (
            <span />
          )}
        </SideAndMain>
      </Section>

      {/* Wide components */}
      {wideRegion && wideRegion.components && wideRegion.components.length > 0 && (
        /* Only render first wide component */
        <ComponentRenderer components={[wideRegion.components[0]]} width="wide" />
      )}
    </>
  );
};

export default ResourceArticlePage;

export const Head = ({ data }: HeadProps<ResourceArticlePageProps>) => {
  const { seoSettings, feed } = data.page;
  return (
    <SEO
      data={seoSettings}
      image={feed?.featuredImage?.localFile?.resize?.src ? feed.featuredImage.localFile.resize.src : undefined}
    />
  );
};

export const pageQuery = graphql`
  query ResourceArticlePageQuery($id: String!) {
    page: kfhContentPageUmbracoType(id: { eq: $id }) {
      pageName
      id
      relativeUrl
      layout {
        ... on KFH_ContentResourceArticleType {
          title
          leadText
          regions {
            ...ComponentRegions
          }
          resourceHub {
            pageName
            relativeUrl
            remoteChildren {
              pageName
              relativeUrl
              id
              layout {
                remoteTypeName
              }
              remoteChildren {
                id
                pageName
                relativeUrl
                layout {
                  remoteTypeName
                }
              }
            }
          }
        }
      }
      parentPages {
        relativeUrl
        pageName
      }
      seoSettings {
        ...SEO
      }
      feed {
        featuredImage {
          ...PageSocialImage
        }
      }
      classification {
        audiences
        services
      }
    }
  }
`;
